import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const BillingCycles = () => {
    const [billingCycles, setBillingCycles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingBillingCycle, setEditingBillingCycle] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [saving, setSaving] = useState(false);

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        periods: '',
        is_active: true,
    });

    useEffect(() => {
        loadBillingCycles();
    }, [currentPage, itemsPerPage]);

    const loadBillingCycles = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getBillingCycles();
            if (response.success) {
                setBillingCycles(response.data || []);
                setTotalPages(Math.ceil((response.data?.total || response.data?.length || 0) / itemsPerPage));
            } else {
                setError(response.error || 'Failed to load billing cycles');
            }
        } catch (error) {
            console.error('Error loading billing cycles:', error);
            setError('Failed to load billing cycles');
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (billingCycle = null) => {
        if (billingCycle) {
            setEditingBillingCycle(billingCycle);
            setFormData({
                name: billingCycle.name || '',
                code: billingCycle.code || '',
                periods: billingCycle.periods !== undefined ? billingCycle.periods : '',
                is_active: billingCycle.is_active !== undefined ? billingCycle.is_active : true,
            });
        } else {
            setEditingBillingCycle(null);
            setFormData({
                name: '',
                code: '',
                periods: '',
                is_active: true,
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingBillingCycle(null);
        setFormData({
            name: '',
            code: '',
            periods: '',
            is_active: true,
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        setSaving(true);
        setError('');

        try {
            const billingCycleData = {
                name: formData.name.trim(),
                code: formData.code.trim(),
                periods: parseInt(formData.periods) || 0,
                is_active: formData.is_active,
            };

            let response;
            if (editingBillingCycle) {
                response = await apiService.updateBillingCycle(editingBillingCycle.id, billingCycleData);
            } else {
                response = await apiService.createBillingCycle(billingCycleData);
            }

            if (response.success) {
                handleCloseModal();
                await loadBillingCycles();
            } else {
                setError(response.error || 'Failed to save billing cycle');
            }
        } catch (error) {
            console.error('Error saving billing cycle:', error);
            setError('Failed to save billing cycle');
        } finally {
            setSaving(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    // Sorting functionality
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
        }
        return sortConfig.direction === 'asc'
            ? <i className="bi bi-arrow-up text-primary ms-1"></i>
            : <i className="bi bi-arrow-down text-primary ms-1"></i>;
    };

    // Sort billing cycles based on current sort configuration
    const sortedBillingCycles = React.useMemo(() => {
        let sortableItems = [...billingCycles];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle different data types
                switch (sortConfig.key) {
                    case 'name':
                    case 'code':
                        aValue = (aValue || '').toString().toLowerCase();
                        bValue = (bValue || '').toString().toLowerCase();
                        break;
                    case 'periods':
                        aValue = parseInt(aValue) || 0;
                        bValue = parseInt(bValue) || 0;
                        break;
                    case 'created_at':
                    case 'last_updated_at':
                        aValue = new Date(aValue || 0);
                        bValue = new Date(bValue || 0);
                        break;
                    case 'is_active':
                        aValue = aValue ? 1 : 0;
                        bValue = bValue ? 1 : 0;
                        break;
                    default:
                        aValue = aValue || '';
                        bValue = bValue || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [billingCycles, sortConfig]);

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Billing Cycles
                        </h2>
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            <i className="bi bi-plus-circle me-2"></i>
                            Add Billing Cycle
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Filter Section */}
            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Status Filter</Form.Label>
                        <Form.Select>
                            <option value="">All Billing Cycles</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Items per page</Form.Label>
                        <Form.Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="10">10 per page</option>
                            <option value="25">25 per page</option>
                            <option value="50">50 per page</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center p-4">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : billingCycles.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bi bi-arrow-repeat display-1"></i>
                                    <div className="mt-2">No billing cycles found</div>
                                </div>
                            ) : (
                                <Table responsive hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('name')}
                                            >
                                                Billing Cycle Name
                                                {getSortIcon('name')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('code')}
                                            >
                                                Billing Cycle Code
                                                {getSortIcon('code')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('periods')}
                                            >
                                                Periods
                                                {getSortIcon('periods')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('is_active')}
                                            >
                                                Status
                                                {getSortIcon('is_active')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('created_at')}
                                            >
                                                Created
                                                {getSortIcon('created_at')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('last_updated_at')}
                                            >
                                                Updated
                                                {getSortIcon('last_updated_at')}
                                            </th>

                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedBillingCycles.map((billingCycle) => (
                                            <tr key={billingCycle.id}>
                                                <td>
                                                    <div className="fw-medium">{billingCycle.name}</div>
                                                </td>
                                                <td>
                                                    <code>{billingCycle.code}</code>
                                                </td>
                                                <td>{billingCycle.periods}</td>
                                                <td>
                                                    <Badge
                                                        bg={billingCycle.is_active ? 'success' : 'secondary'}
                                                        className="text-uppercase"
                                                    >
                                                        {billingCycle.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </td>
                                                <td>{billingCycle.created_at ? apiService.formatDate(billingCycle.created_at) : '-'}</td>
                                                <td>{billingCycle.last_updated_at ? apiService.formatDate(billingCycle.last_updated_at) : '-'}</td>

                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => handleShowModal(billingCycle)}
                                                            title="Edit"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, billingCycles.length)} of {billingCycles.length} entries
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPage}
                                                onClick={() => handlePageChange(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingBillingCycle ? 'Edit Billing Cycle' : 'Add Billing Cycle'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Billing Cycle Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. Monthly"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Billing Cycle Code *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. MONTHLY"
                                    />
                                    <Form.Text className="text-muted">
                                        Unique code (e.g. MONTHLY, QUARTERLY, ANNUAL)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Periods *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="periods"
                                        value={formData.periods}
                                        onChange={handleInputChange}
                                        required
                                        min="1"
                                        placeholder="e.g. 12"
                                    />
                                    <Form.Text className="text-muted">
                                        Number of periods in the billing cycle
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="is_active"
                                id="is_active"
                                label="Active Billing Cycle"
                                checked={formData.is_active}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Billing Cycle'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default BillingCycles;
